import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    Radio,
    RadioGroup,
    Switch,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'

import { useAptConfig } from '../../hooks/useAptConfig'
import { useUser } from '../../hooks/useUser'
import { FormSaveAction } from './FormSaveButton'
import { SetupArtifactShell } from './SetupArtifactShell'
import { SelectedSetupConfig, SetupConfigOption } from './setupArtifacts'
import {
    FloorPlanRentKind,
    PropertyDataDraft,
    PropertyDataValueKey,
    applyPrimaryAddressToDraft,
    emptyFloorPlanRent,
    emptyPropertyDataDraft,
    formatPrimaryAddress,
    OccupancyPolicyDraft,
    ParkingPolicyDraft,
} from './propertyDataPack'
import { FhaAckDialog } from '../ApplicationWorkbook/FhaAckDialog'
import {
    ANIMAL_TYPES,
    DOG_ANIMAL_TYPE_ID,
    DOG_BREEDS,
    PET_FEE_FIELDS,
    POPULAR_DOG_BREED_IDS,
    PetFeeKey,
    PetsPolicyDraft,
    allDogBreedIds,
} from './petPolicy'

const useStyles = makeStyles((theme: Theme) => ({
    body: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        width: '100%',
        padding: theme.spacing(0.5, 0, 1),
    },
    intro: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.45,
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
    },
    sectionTitle: {
        fontSize: '0.875rem',
        fontWeight: 600,
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: theme.spacing(1.25),
    },
    typeGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: theme.spacing(0.75),
        alignItems: 'start',
    },
    typeCell: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
    },
    typeLabel: {
        '& .MuiFormControlLabel-label': {
            fontSize: '0.8125rem',
        },
    },
    capLabel: {
        marginLeft: theme.spacing(3),
        '& .MuiFormControlLabel-label': {
            fontSize: '0.75rem',
            color: theme.palette.text.secondary,
        },
    },
    breedToolbar: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        alignItems: 'center',
    },
    breedActions: {
        display: 'flex',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    breedList: {
        maxHeight: 320,
        overflowY: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: theme.spacing(0.5, 1),
    },
    breedGroupLabel: {
        fontSize: '0.75rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        padding: theme.spacing(0.75, 0, 0.25),
    },
    breedCount: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    rentRow: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 160px 160px',
        gap: theme.spacing(1),
        alignItems: 'center',
    },
    rentColLabel: {
        fontSize: '0.75rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
    },
    floorPlanName: {
        fontSize: '0.875rem',
        fontWeight: 600,
    },
    floorPlanMeta: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    empty: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
    },
    fieldHint: {
        fontSize: '0.75rem',
        lineHeight: 1.35,
    },
}))

type FloorPlanRow = {
    id: number
    name: string
    shows_on_schedule?: boolean
    bed_count?: number | null
    bath_count?: number | null
}

function floorPlanMeta(row: FloorPlanRow): string {
    const parts: string[] = []
    if (row.bed_count != null) parts.push(`${row.bed_count} bed`)
    if (row.bath_count != null) parts.push(`${row.bath_count} bath`)
    return parts.join(' · ')
}

function matchesBreedQuery(label: string, query: string): boolean {
    if (!query) return true
    return label.toLowerCase().includes(query)
}

export const PropertyDataSetupTab: React.FC<{
    selected: SelectedSetupConfig | null
    onSelected: (config: SelectedSetupConfig) => void
    onClearSelection: () => void
    existing: SetupConfigOption[]
    onExistingChange: (next: SetupConfigOption[]) => void
    draft: PropertyDataDraft
    onDraftChange: (next: PropertyDataDraft) => void
    contentLocked?: boolean
    artifactEditable?: boolean
    saveAction?: FormSaveAction
}> = ({
    selected,
    onSelected,
    onClearSelection,
    existing,
    onExistingChange,
    draft,
    onDraftChange,
    contentLocked = false,
    artifactEditable = true,
    saveAction,
}) => {
    const classes = useStyles()
    const readOnly =
        !!selected?.orgMandated || contentLocked || !artifactEditable
    const { workspaceUser } = useUser()
    const primaryAddress = workspaceUser?.active_workspace?.primary_address
    const formattedAddress = formatPrimaryAddress(primaryAddress)
    const { unitConfigList } = useAptConfig({ unitConfigList: true })
    const floorPlans = ((unitConfigList || []) as FloorPlanRow[]).filter(
        (row) => row.shows_on_schedule !== false,
    )
    const empty = emptyPropertyDataDraft()
    const values = draft?.values ?? empty.values
    const rents = draft?.floor_plan_rents ?? {}
    const pets = draft?.pets ?? empty.pets
    const parking = draft?.parking ?? empty.parking
    const occupancy = draft?.occupancy ?? empty.occupancy
    const [breedQuery, setBreedQuery] = useState('')
    const [coedAckOpen, setCoedAckOpen] = useState(false)

    useEffect(() => {
        if (!selected || readOnly) return
        if (values.legal_property_address === formattedAddress) return
        onDraftChange(
            applyPrimaryAddressToDraft(
                {
                    values,
                    floor_plan_rents: rents,
                    pets,
                    parking,
                    occupancy,
                },
                primaryAddress,
            ),
        )
    }, [selected?.id, formattedAddress, readOnly])

    const setDraft = (next: Partial<PropertyDataDraft>) => {
        onDraftChange({
            values: next.values ?? values,
            floor_plan_rents: next.floor_plan_rents ?? rents,
            pets: next.pets ?? pets,
            parking: next.parking ?? parking,
            occupancy: next.occupancy ?? occupancy,
        })
    }

    const setValue = (key: PropertyDataValueKey, value: string) => {
        setDraft({
            values: { ...values, [key]: value },
        })
    }

    const setRent = (
        unitConfigId: number,
        kind: FloorPlanRentKind,
        value: string,
    ) => {
        const key = String(unitConfigId)
        const current = rents[key] ?? emptyFloorPlanRent()
        setDraft({
            floor_plan_rents: {
                ...rents,
                [key]: {
                    new: current.new,
                    renewal: current.renewal,
                    [kind]: value,
                },
            },
        })
    }

    const setPets = (next: PetsPolicyDraft) => {
        setDraft({ pets: next })
    }

    const setParking = (next: ParkingPolicyDraft) => {
        setDraft({ parking: next })
    }

    const setOccupancy = (next: OccupancyPolicyDraft) => {
        setDraft({ occupancy: next })
    }

    const setPetFee = (key: PetFeeKey, value: string) => {
        setPets({ ...pets, [key]: value })
    }

    const allowedTypes = new Set(pets.allowed_animal_types)
    const dogAllowed = allowedTypes.has(DOG_ANIMAL_TYPE_ID)
    const allowAllBreeds = pets.dog_breeds.mode === 'all'
    const selectedBreedIds = new Set(pets.dog_breeds.ids)
    const query = breedQuery.trim().toLowerCase()

    const visibleBreeds = useMemo(() => {
        const popularSet = new Set<string>(POPULAR_DOG_BREED_IDS)
        const matching = DOG_BREEDS.filter((row) =>
            matchesBreedQuery(row.label, query),
        )
        if (query) {
            return { popular: [] as typeof DOG_BREEDS, rest: matching }
        }
        return {
            popular: matching.filter((row) => popularSet.has(row.id)),
            rest: matching.filter((row) => !popularSet.has(row.id)),
        }
    }, [query])

    const capSet = String(pets.max_pets_per_unit ?? '').trim() !== ''

    const toggleAnimalType = (typeId: string, checked: boolean) => {
        const next = pets.allowed_animal_types.filter((id) => id !== typeId)
        if (checked) next.push(typeId)
        setPets({
            ...pets,
            allowed_animal_types: ANIMAL_TYPES.map(
                (row) => row.id,
            ).filter((id) => next.includes(id)),
        })
    }

    const setCountsTowardCap = (typeId: string, counted: boolean) => {
        setPets({
            ...pets,
            counts_toward_cap: {
                ...pets.counts_toward_cap,
                [typeId]: counted,
            },
        })
    }

    const toggleAllowAllBreeds = (checked: boolean) => {
        setPets({
            ...pets,
            dog_breeds: checked
                ? { mode: 'all', ids: [] }
                : { mode: 'allowlist', ids: allDogBreedIds() },
        })
    }

    const toggleBreed = (breedId: string, checked: boolean) => {
        const ids = pets.dog_breeds.ids.filter((id) => id !== breedId)
        if (checked) ids.push(breedId)
        setPets({
            ...pets,
            dog_breeds: { mode: 'allowlist', ids },
        })
    }

    const selectAllBreeds = (checked: boolean) => {
        setPets({
            ...pets,
            dog_breeds: {
                mode: 'allowlist',
                ids: checked ? allDogBreedIds() : [],
            },
        })
    }

    return (
        <SetupArtifactShell
            kind="property_data"
            selected={selected}
            onSelected={onSelected}
            onClearSelection={onClearSelection}
            existing={existing}
            onExistingChange={onExistingChange}
            contentLocked={contentLocked}
            readinessLabel={selected ? 'Property data selected' : undefined}
            saveAction={readOnly ? undefined : saveAction}
        >
            <Box className={classes.body}>
                <Typography className={classes.intro}>
                    These values fill Kind=Property fields on signable docs.
                    Legal property address comes from this property&apos;s
                    primary address. Rent (the installment) uses the
                    applicant&apos;s floor plan.
                </Typography>
                {selected ? (
                    <>
                        <Box className={classes.section}>
                            <Typography className={classes.sectionTitle}>
                                Identity
                            </Typography>
                            <Box className={classes.grid}>
                                <TextField
                                    label="Legal property name"
                                    size="small"
                                    variant="outlined"
                                    value={values.legal_property_name}
                                    disabled={readOnly}
                                    onChange={(event) =>
                                        setValue(
                                            'legal_property_name',
                                            event.target.value,
                                        )
                                    }
                                />
                                <TextField
                                    label="Legal property address"
                                    size="small"
                                    variant="outlined"
                                    value={
                                        readOnly
                                            ? values.legal_property_address
                                            : formattedAddress
                                    }
                                    disabled
                                    helperText={
                                        readOnly
                                            ? undefined
                                            : formattedAddress
                                            ? "From this property's primary address."
                                            : 'Set the property primary address in Property Details.'
                                    }
                                />
                                <TextField
                                    label="Leasing manager email"
                                    type="email"
                                    size="small"
                                    variant="outlined"
                                    value={values.leasing_manager_email ?? ''}
                                    disabled={readOnly}
                                    onChange={(event) =>
                                        setValue(
                                            'leasing_manager_email',
                                            event.target.value,
                                        )
                                    }
                                    helperText="This is the email ready-to-sign leases will be sent to."
                                    FormHelperTextProps={{
                                        className: classes.fieldHint,
                                    }}
                                />
                            </Box>
                        </Box>
                        <Box className={classes.section}>
                            <Typography className={classes.sectionTitle}>
                                Lease terms
                            </Typography>
                            <Typography className={classes.fieldHint}>
                                Lease start, lease end, move-in, and move-out
                                come from the lease term selected when the
                                season is created.
                            </Typography>
                            <Box className={classes.grid}>
                                <TextField
                                    label="Number of installments"
                                    type="number"
                                    size="small"
                                    variant="outlined"
                                    inputProps={{ min: 0, step: 1 }}
                                    value={values.number_of_installments}
                                    disabled={readOnly}
                                    onChange={(event) =>
                                        setValue(
                                            'number_of_installments',
                                            event.target.value,
                                        )
                                    }
                                />
                                <TextField
                                    label="Number of keys"
                                    type="number"
                                    size="small"
                                    variant="outlined"
                                    inputProps={{ min: 0, step: 1 }}
                                    value={values.number_of_keys}
                                    disabled={readOnly}
                                    onChange={(event) =>
                                        setValue(
                                            'number_of_keys',
                                            event.target.value,
                                        )
                                    }
                                />
                            </Box>
                        </Box>
                        <Box className={classes.section}>
                            <Typography className={classes.sectionTitle}>
                                Rent by floor plan
                            </Typography>
                            <Typography className={classes.intro}>
                                New-lease offers use new tenant rent. Renewal
                                offers use renewal rent.
                            </Typography>
                            {floorPlans.length === 0 ? (
                                <Typography className={classes.empty}>
                                    No floor plans are showing on the schedule
                                    yet. Add them in unit configuration.
                                </Typography>
                            ) : (
                                <>
                                    <Box className={classes.rentRow}>
                                        <span />
                                        <Typography
                                            className={classes.rentColLabel}
                                        >
                                            New tenant
                                        </Typography>
                                        <Typography
                                            className={classes.rentColLabel}
                                        >
                                            Renewal
                                        </Typography>
                                    </Box>
                                    {floorPlans.map((row) => {
                                        const rent =
                                            rents[String(row.id)] ??
                                            emptyFloorPlanRent()
                                        return (
                                            <Box
                                                key={row.id}
                                                className={classes.rentRow}
                                            >
                                                <Box>
                                                    <Typography
                                                        className={
                                                            classes.floorPlanName
                                                        }
                                                    >
                                                        {row.name}
                                                    </Typography>
                                                    {floorPlanMeta(row) ? (
                                                        <Typography
                                                            className={
                                                                classes.floorPlanMeta
                                                            }
                                                        >
                                                            {floorPlanMeta(row)}
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                                <TextField
                                                    size="small"
                                                    variant="outlined"
                                                    value={rent.new}
                                                    disabled={readOnly}
                                                    inputProps={{
                                                        'aria-label': `${row.name} new tenant rent`,
                                                    }}
                                                    onChange={(event) =>
                                                        setRent(
                                                            row.id,
                                                            'new',
                                                            event.target.value,
                                                        )
                                                    }
                                                />
                                                <TextField
                                                    size="small"
                                                    variant="outlined"
                                                    value={rent.renewal}
                                                    disabled={readOnly}
                                                    inputProps={{
                                                        'aria-label': `${row.name} renewal rent`,
                                                    }}
                                                    onChange={(event) =>
                                                        setRent(
                                                            row.id,
                                                            'renewal',
                                                            event.target.value,
                                                        )
                                                    }
                                                />
                                            </Box>
                                        )
                                    })}
                                </>
                            )}
                        </Box>
                        <Box className={classes.section}>
                            <Typography className={classes.sectionTitle}>
                                Occupancy
                            </Typography>
                            <Typography className={classes.intro}>
                                When coed rooms are not allowed, matching will
                                not place male and female applicants in the same
                                unit. Applicants who answer non-binary or prefer
                                not to say are held for you to place manually.
                            </Typography>
                            <FormControl
                                component="fieldset"
                                disabled={readOnly}
                            >
                                <RadioGroup
                                    value={
                                        occupancy.allow_coed_rooms
                                            ? 'allowed'
                                            : 'disallowed'
                                    }
                                    onChange={(event) => {
                                        const allowed =
                                            event.target.value === 'allowed'
                                        if (
                                            occupancy.allow_coed_rooms &&
                                            !allowed
                                        ) {
                                            setCoedAckOpen(true)
                                            return
                                        }
                                        setOccupancy({
                                            allow_coed_rooms: allowed,
                                        })
                                    }}
                                >
                                    <FormControlLabel
                                        value="disallowed"
                                        control={<Radio color="primary" />}
                                        label="Not allowed — do not mix male and female in a unit"
                                    />
                                    <FormControlLabel
                                        value="allowed"
                                        control={<Radio color="primary" />}
                                        label="Allowed — matching ignores gender"
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Box>
                        <Box className={classes.section}>
                            <Typography className={classes.sectionTitle}>
                                Pets
                            </Typography>
                            <Typography className={classes.intro}>
                                Applicants can only choose animal types and dog
                                breeds you allow here. Service and assistance
                                animals are handled separately.
                            </Typography>
                            <Box className={classes.grid}>
                                <TextField
                                    label="Max pets per unit"
                                    type="number"
                                    size="small"
                                    variant="outlined"
                                    inputProps={{ min: 0, step: 1 }}
                                    value={pets.max_pets_per_unit ?? ''}
                                    disabled={readOnly}
                                    onChange={(event) =>
                                        setPets({
                                            ...pets,
                                            max_pets_per_unit:
                                                event.target.value,
                                        })
                                    }
                                    helperText="Leave blank for no limit. Roommate suggestions will not exceed this count of counting animals. You can still place someone manually."
                                    FormHelperTextProps={{
                                        className: classes.fieldHint,
                                    }}
                                />
                            </Box>
                            <Box className={classes.typeGrid}>
                                {ANIMAL_TYPES.map((row) => (
                                    <Box
                                        key={row.id}
                                        className={classes.typeCell}
                                    >
                                        <FormControlLabel
                                            className={classes.typeLabel}
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    size="small"
                                                    checked={allowedTypes.has(
                                                        row.id,
                                                    )}
                                                    disabled={readOnly}
                                                    onChange={(event) =>
                                                        toggleAnimalType(
                                                            row.id,
                                                            event.target
                                                                .checked,
                                                        )
                                                    }
                                                />
                                            }
                                            label={row.label}
                                        />
                                        {capSet && allowedTypes.has(row.id) ? (
                                            <FormControlLabel
                                                className={classes.capLabel}
                                                control={
                                                    <Checkbox
                                                        color="primary"
                                                        size="small"
                                                        checked={
                                                            pets
                                                                .counts_toward_cap[
                                                                row.id
                                                            ] !== false
                                                        }
                                                        disabled={readOnly}
                                                        onChange={(event) =>
                                                            setCountsTowardCap(
                                                                row.id,
                                                                event.target
                                                                    .checked,
                                                            )
                                                        }
                                                    />
                                                }
                                                label="Count toward unit pet cap"
                                            />
                                        ) : null}
                                    </Box>
                                ))}
                            </Box>
                            <Box className={classes.grid}>
                                {PET_FEE_FIELDS.map((row) => (
                                    <TextField
                                        key={row.id}
                                        label={row.label}
                                        size="small"
                                        variant="outlined"
                                        value={pets[row.id] ?? ''}
                                        disabled={readOnly}
                                        onChange={(event) =>
                                            setPetFee(
                                                row.id,
                                                event.target.value,
                                            )
                                        }
                                    />
                                ))}
                            </Box>
                            {dogAllowed ? (
                                <>
                                    <FormControlLabel
                                        className={classes.typeLabel}
                                        control={
                                            <Switch
                                                color="primary"
                                                size="small"
                                                checked={allowAllBreeds}
                                                disabled={readOnly}
                                                onChange={(event) =>
                                                    toggleAllowAllBreeds(
                                                        event.target.checked,
                                                    )
                                                }
                                            />
                                        }
                                        label="Allow all dog breeds"
                                    />
                                    {allowAllBreeds ? (
                                        <Typography className={classes.empty}>
                                            Applicants will see the full breed
                                            list. Turn this off to choose
                                            allowed breeds.
                                        </Typography>
                                    ) : (
                                        <>
                                            <Box
                                                className={classes.breedToolbar}
                                            >
                                                <TextField
                                                    label="Search breeds"
                                                    size="small"
                                                    variant="outlined"
                                                    value={breedQuery}
                                                    disabled={readOnly}
                                                    onChange={(event) =>
                                                        setBreedQuery(
                                                            event.target.value,
                                                        )
                                                    }
                                                />
                                                <Box
                                                    className={
                                                        classes.breedActions
                                                    }
                                                >
                                                    <Button
                                                        size="small"
                                                        disabled={readOnly}
                                                        onClick={() =>
                                                            selectAllBreeds(
                                                                true,
                                                            )
                                                        }
                                                    >
                                                        Select all
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        disabled={readOnly}
                                                        onClick={() =>
                                                            selectAllBreeds(
                                                                false,
                                                            )
                                                        }
                                                    >
                                                        Clear all
                                                    </Button>
                                                </Box>
                                                <Typography
                                                    className={
                                                        classes.breedCount
                                                    }
                                                >
                                                    {selectedBreedIds.size} of{' '}
                                                    {DOG_BREEDS.length} allowed
                                                </Typography>
                                            </Box>
                                            <Typography
                                                className={classes.empty}
                                            >
                                                If a breed is not checked, it is
                                                not allowed at this property.
                                            </Typography>
                                            {selectedBreedIds.size === 0 ? (
                                                <Typography
                                                    className={classes.empty}
                                                >
                                                    Select at least one breed,
                                                    or turn on allow all.
                                                </Typography>
                                            ) : null}
                                            <FormGroup
                                                className={classes.breedList}
                                            >
                                                {visibleBreeds.popular
                                                    .length ? (
                                                    <Typography
                                                        className={
                                                            classes.breedGroupLabel
                                                        }
                                                    >
                                                        Popular
                                                    </Typography>
                                                ) : null}
                                                {visibleBreeds.popular.map(
                                                    (row) => (
                                                        <FormControlLabel
                                                            key={row.id}
                                                            className={
                                                                classes.typeLabel
                                                            }
                                                            control={
                                                                <Checkbox
                                                                    color="primary"
                                                                    size="small"
                                                                    checked={selectedBreedIds.has(
                                                                        row.id,
                                                                    )}
                                                                    disabled={
                                                                        readOnly
                                                                    }
                                                                    onChange={(
                                                                        event,
                                                                    ) =>
                                                                        toggleBreed(
                                                                            row.id,
                                                                            event
                                                                                .target
                                                                                .checked,
                                                                        )
                                                                    }
                                                                />
                                                            }
                                                            label={row.label}
                                                        />
                                                    ),
                                                )}
                                                {visibleBreeds.rest.length &&
                                                visibleBreeds.popular.length ? (
                                                    <Typography
                                                        className={
                                                            classes.breedGroupLabel
                                                        }
                                                    >
                                                        All breeds
                                                    </Typography>
                                                ) : null}
                                                {visibleBreeds.rest.map(
                                                    (row) => (
                                                        <FormControlLabel
                                                            key={row.id}
                                                            className={
                                                                classes.typeLabel
                                                            }
                                                            control={
                                                                <Checkbox
                                                                    color="primary"
                                                                    size="small"
                                                                    checked={selectedBreedIds.has(
                                                                        row.id,
                                                                    )}
                                                                    disabled={
                                                                        readOnly
                                                                    }
                                                                    onChange={(
                                                                        event,
                                                                    ) =>
                                                                        toggleBreed(
                                                                            row.id,
                                                                            event
                                                                                .target
                                                                                .checked,
                                                                        )
                                                                    }
                                                                />
                                                            }
                                                            label={row.label}
                                                        />
                                                    ),
                                                )}
                                            </FormGroup>
                                        </>
                                    )}
                                </>
                            ) : null}
                        </Box>
                        <Box className={classes.section}>
                            <Typography className={classes.sectionTitle}>
                                Parking
                            </Typography>
                            <Typography className={classes.intro}>
                                Added to the monthly total when the applicant
                                needs parking.
                            </Typography>
                            <Box className={classes.grid}>
                                <TextField
                                    label="Monthly parking fee"
                                    size="small"
                                    variant="outlined"
                                    value={parking.monthly_fee}
                                    disabled={readOnly}
                                    onChange={(event) =>
                                        setParking({
                                            monthly_fee: event.target.value,
                                        })
                                    }
                                />
                            </Box>
                        </Box>
                    </>
                ) : null}
            </Box>
            <FhaAckDialog
                open={coedAckOpen}
                title="Disallow coed rooms"
                body="Matching will not place male and female applicants in the same unit. Applicants who answer non-binary or prefer not to say will be left for you to place manually. Do not use this setting to deny housing or change terms."
                confirmLabel="Disallow coed rooms"
                onClose={() => setCoedAckOpen(false)}
                onConfirm={() => {
                    setOccupancy({ allow_coed_rooms: false })
                    setCoedAckOpen(false)
                }}
            />
        </SetupArtifactShell>
    )
}
